import React, { useCallback, useState } from "react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { cn } from "../../../../lib/utils";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import NodeControl from "../components/node-control.js";
import { useNodeDataUpdate } from "../../hooks/use-node-data-update.js";
import { AlertCircle } from "lucide-react";

/** Validate phone number in E.164 format: +1-555-0123 or +1555012 */
const isValidE164PhoneNumber = (phone) => {
  if (!phone) return false;
  const e164Regex = /^\+?[1-9]\d{1,14}$/;
  const cleaned = phone.replace(/[\s\-()]/g, "");
  return e164Regex.test(cleaned);
};

const HumanAgentHandoff = ({
  id,
  data,
  selected,
}) => {
  const { updateNodeData } = useReactFlow();
  const { handleNodeDataUpdate } = useNodeDataUpdate();
  const [phoneError, setPhoneError] = useState(false);

  const handlePhoneChange = useCallback(
    (value) => {
      handleNodeDataUpdate({ id, data: { inboundHumanNumber: value } });
      if (value) {
        setPhoneError(!isValidE164PhoneNumber(value));
      } else {
        setPhoneError(false);
      }
    },
    [id, handleNodeDataUpdate]
  );

  const handlePhoneBlur = useCallback(
    (value) => {
      const trimmedValue = value.trim();
      if (trimmedValue && !isValidE164PhoneNumber(trimmedValue)) {
        setPhoneError(true);
      }
      updateNodeData(id, { inboundHumanNumber: trimmedValue });
    },
    [id, updateNodeData]
  );

  const handleTimeoutChange = useCallback(
    (value) => {
      const num = Math.max(1, Math.min(30, parseInt(value) || 10));
      handleNodeDataUpdate({ id, data: { ringTimeoutSeconds: num } });
    },
    [id, handleNodeDataUpdate]
  );

  const handleGreetingChange = useCallback(
    (value) => {
      handleNodeDataUpdate({ id, data: { greetingMessage: value } });
    },
    [id, handleNodeDataUpdate]
  );

  const timeout = data.ringTimeoutSeconds || 10;
  const phoneNumber = data.inboundHumanNumber || "";
  const greeting = data.greetingMessage || "";

  return (
    <div
      className={cn(
        "w-[320px] group inline-flex flex-col items-start justify-start rounded-lg border-2 border-blue-400/60 bg-[#E3F2FD] shadow-sm",
        selected && "outline-2 outline-blue-500/70"
      )}
    >
      <Handle className="!top-5" type="target" position={Position.Left} />

      {/* Header */}
      <header className="flex w-full items-center justify-start gap-2 px-3 pb-2 pt-3">
        <span className="shrink-0 rounded-md bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
          📞 Human Handoff
        </span>
        <NodeControl id={id} selected={selected} />
      </header>

      {/* Content */}
      <div className="flex w-full flex-col items-start justify-start gap-3 p-3">
        {/* Info text */}
        <p className="text-[11px] text-blue-800/80">
          Routes inbound calls to a human agent. If no answer within timeout, falls back to next AI node.
        </p>

        {/* Human Phone Number */}
        <div className="w-full">
          <label className="block text-xs font-medium text-blue-700 mb-1">
            Inbound Human Phone Number *
          </label>
          <Input
            className={cn(
              "nodrag nowheel border-blue-200/80 bg-white/90 text-sm",
              phoneError && "border-red-500 border-2"
            )}
            type="tel"
            value={phoneNumber}
            placeholder="+1-555-0123 or +1555012"
            onChange={(evt) => handlePhoneChange(evt.target.value)}
            onBlur={(evt) => handlePhoneBlur(evt.target.value)}
          />
          {phoneError && (
            <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>Invalid E.164 format (e.g., +1-555-0123)</span>
            </div>
          )}
        </div>

        {/* Ring Timeout */}
        <div className="w-full">
          <label className="block text-xs font-medium text-blue-700 mb-2">
            Ring Timeout: <span className="font-bold text-blue-600">{timeout}s</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="1"
              max="30"
              value={timeout}
              onChange={(evt) => handleTimeoutChange(evt.target.value)}
              className="nodrag nowheel flex-1 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-xs text-blue-700 font-medium w-8">
              {timeout}s
            </span>
          </div>
          <p className="text-[10px] text-blue-700/60 mt-1">
            How long to ring before falling back to AI (1-30 seconds)
          </p>
        </div>

        {/* Greeting Message */}
        <div className="w-full">
          <label className="block text-xs font-medium text-blue-700 mb-1">
            Greeting Message (Optional)
          </label>
          <Textarea
            value={greeting}
            onChange={(evt) => handleGreetingChange(evt.target.value)}
            className="nodrag nowheel resize-none bg-white border-blue-200/80 text-sm min-h-20 max-h-40"
            placeholder="e.g., Connecting you to an agent..."
          />
          <p className="text-[10px] text-blue-700/60 mt-1">
            Message played to customer while dialing. Leave blank for silent mode.
          </p>
        </div>

        {/* Status Badge */}
        <div className="mt-2 w-full p-2 rounded-md bg-blue-50/80 border border-blue-100">
          <p className="text-xs text-blue-700">
            <strong>Status:</strong> {phoneError ? "⚠️ Invalid phone" : phoneNumber ? "✅ Ready" : "❌ Missing phone"}
          </p>
        </div>
      </div>

      {/* Source handle for transition to next node */}
      <Handle
        className="!border-blue-500 !bg-blue-50"
        type="source"
        position={Position.Right}
        id="success"
      />
    </div>
  );
};

export default React.memo(HumanAgentHandoff);
